import React, { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Col, Container, Placeholder, Row } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { User } from '../../../../../models/User'
import { getUserDisplayName } from '../../common/aifeatures/utils'
import { UserTag } from '../../UserTag'
import { SpeakingDistributionIcon } from './SpeakingDistributionIcon'
import { parseMetadata } from '../utils'
import { CONSENT_EXCLUDED_ROLES } from './AssignmentRoleNameMapping'
import { AllocationModal } from '../../AllocationModal'
import { AddParticipantModal } from './AddParticipantModal'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import buttonStyles from '../../../../../components/Button/Button.module.scss'
import {
  selectRoomSession,
  selectRoomSessionCreatedBy,
  selectRoomSessionHasTranscription,
  selectRoomSessionIsLoading,
  selectRoomSessionPollingEnabled,
  setRoomSession,
  startRoomSessionPolling,
  stopRoomSessionPolling,
} from '../../../../../store/meetingSummary/roomSession'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { selectAiResultPollingEnabled } from '../../../../../store/meetingSummary/aiResults'

import { useGetMeQuery } from '../../../../../store/user/user'
import { useDeleteManualParticipantMutation, useLazyGetRoomSessionQuery } from '../../../../../store/media/media'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomTrackingDetails } from '../../tracking'
import { ConsentIcon } from '../../../../../components/Consent/ConsentIcon'
import { ConsentModal } from '../../../../../components/Consent/ConsentModal'
import { UserConsent } from '../../../../../components/Consent/model'
import { useZocksQuota } from '../../../../../components/ScopedBlock/hooks'
import { ROLES_PER_PAGE, useMeetingUsers, UsersConsent } from '../hooks/users'
import {
  selectMeetingSummaryAgentAiAgentStatus,
  selectMeetingSummaryAgentLoadingInteraction,
  selectMeetingSummaryAgentShouldPoll,
} from '../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { selectSessionMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { AIAgentStatus } from '../../../../../models/AIAgentModel'
import './ConfigureAssignment.scss'

import { SentimentAnalysisMetadata } from '../SentimentAnalysis/model'
import { SentimentDistributionInfo } from '../SentimentAnalysis/SentimentDistribution'
import { SentimentTrendChart } from '../SentimentAnalysis/SentimentTrendChart'
import { SentimentAnalysisToggleButton } from './SentimentAnalysisToggleButton'
import { CONTACT_UPDATE_SUPPORTED_INTEGRATIONS, IntegrationServiceId } from '../../../../../common/integrations'
import { UpdateExternalMeetingContactModal } from '../../../../../views/UpdateExternalContact/UpdateExternalContact'
import { selectShouldReload, setShouldReload, setUpdateStartedFrom } from '../../../../../store/meetingSummary/contactUpdate'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { useAccount } from '../../../../../common/useAccount'
import { useConsentCheck } from '../hooks'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { ContactLanguageDisplay } from '../MeetingInfo/Language'
import { useMultiLanguageEnabled } from '../../../../../common/useMultiLanguageEnabled'
import { useSyncStatuses } from '../ExternalUpdates/hooks'
import { type ManualParticipant } from '../../../../../models/ManualParticipant'
import { DeleteManualParticipantModal } from './DeleteManualParticipantModal'
import { EditManualParticipantModal } from './EditManualParticipantModal'
import { ManualParticipantIndicator } from './ManualParticipantIndicator'
import { showToast } from '../../../../../store/meetingSummary/display'

interface ManualParticipantTarget {
  user: User
  manualParticipant: ManualParticipant
}

export const ConfigureAssignment: React.FC = () => {
  const { tracked } = useTracking()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { hasQuota } = useZocksQuota()

  const consentTrackingAvailable = hasQuota('ai.userConsentTracking.value')

  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const roomSession = useAppSelector(selectRoomSession)
  const roomSessionCreatedBy = useAppSelector(selectRoomSessionCreatedBy)
  const hasTranscription = useAppSelector(selectRoomSessionHasTranscription)
  const aiResultsPolling = useAppSelector(selectAiResultPollingEnabled)
  const isLoading = useAppSelector(selectRoomSessionIsLoading)
  const meetingSummaryAgentStatus = useAppSelector(selectMeetingSummaryAgentAiAgentStatus)
  const meetingSummaryAgentPolling = useAppSelector(selectMeetingSummaryAgentShouldPoll)
  const meetingSummaryAgentLoadingInteraction = useAppSelector(selectMeetingSummaryAgentLoadingInteraction)
  const sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)
  const roomSessionPollingEnabled = useAppSelector(selectRoomSessionPollingEnabled)
  const { accountId, account } = useAccount({ refetchOnMountOrArgChange: true })
  const { roomId, id: sessionId } = roomSession || {}
  const { data: currentUser } = useGetMeQuery({ accountId })
  const [triggerGetRoomSession] = useLazyGetRoomSessionQuery()
  const [deleteManualParticipant, { isLoading: deletingManualParticipant }] = useDeleteManualParticipantMutation()

  const [showAssignmentModal, setShowAssignmentModal] = useState<boolean>(false)
  const [showUpdateParticipantModal, setShowUpdateParticipantModal] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedIntegration, setSelectedIntegration] = useState<IntegrationServiceId | null>(null)
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [currentConsent, setCurrentConsent] = useState<UserConsent | null>(null)
  const [showParticipantSentimentAnalysis, setShowParticipantSentimentAnalysis] = useState<boolean>(false)
  const [showAddMissingParticipantModal, setShowAddMissingParticipantModal] = useState<boolean>(false)
  const [manualParticipantToDelete, setManualParticipantToDelete] = useState<ManualParticipantTarget | null>(null)
  const [manualParticipantToEdit, setManualParticipantToEdit] = useState<ManualParticipantTarget | null>(null)

  const shouldReload = useAppSelector(selectShouldReload)

  useEffect(() => {
    if (!showUpdateParticipantModal && shouldReload) {
      dispatch(setShouldReload(false))
      navigate(0)
    }
  }, [showUpdateParticipantModal, shouldReload, dispatch, navigate])

  const refreshRoomSession = useCallback(async () => {
    if (!roomId || !sessionId) {
      return
    }

    const { data: updatedRoomSession } = await triggerGetRoomSession({ accountId, roomId, sessionId })
    if (updatedRoomSession?.data) {
      dispatch(setRoomSession(updatedRoomSession.data))
    }
  }, [accountId, dispatch, roomId, sessionId, triggerGetRoomSession])

  const handleCancelParticipantUpdate = useCallback(() => {
    setSelectedUser(null)
    setSelectedIntegration(null)
    setShowUpdateParticipantModal(false)
    dispatch(setUpdateStartedFrom(null))
  }, [dispatch])

  const handleSaveParticipantUpdate = useCallback(() => {
    setSelectedUser(null)
    setSelectedIntegration(null)
    setShowUpdateParticipantModal(false)
    dispatch(setUpdateStartedFrom(null))
  }, [dispatch])

  const { usersConsent } = useConsentCheck({ roomId, sessionId, skip: !consentTrackingAvailable })
  const consentWarningsEnabled = consentTrackingAvailable && !!account?.effectiveConfig.complianceConfig.userConsentTrackingEnabled
  const currentUserIsMeetingOwner = roomSessionCreatedBy?.id === currentUserId
  const canManagePrivateTeammateAccess = !roomSession?.isPrivate || currentUserIsMeetingOwner

  const {
    usersByRoles,
    usersByRolesOnCurrentPage,
    currentPage,
    pages,
    nextPage,
    previousPage,
    speakingDistribution,
    getSpeakingPercentage,
    manualParticipantsByUserId,
    manualParticipantAddedByNames,
  } = useMeetingUsers(roomSession)

  useEffect(() => {
    if (!!Object.keys(speakingDistribution).length && roomSessionPollingEnabled) {
      dispatch(stopRoomSessionPolling())
    }
    if (!Object.keys(speakingDistribution).length && !roomSessionPollingEnabled) {
      dispatch(startRoomSessionPolling())
    }
  }, [dispatch, roomSessionPollingEnabled, speakingDistribution])

  const disableAllocation =
    !hasTranscription ||
    aiResultsPolling ||
    meetingSummaryAgentPolling ||
    !!meetingSummaryAgentLoadingInteraction ||
    meetingSummaryAgentStatus === AIAgentStatus.RUNNING ||
    !sessionMeetingTemplate?.isActive

  const handleShowAssignmentModal = () => {
    setShowAssignmentModal(true)
  }

  const handleSaveNewAssignment = () => {
    setShowAssignmentModal(false)
    navigate(0)
  }

  const handleCancelNewAssignment = () => {
    setShowAssignmentModal(false)
  }

  const _handleUpdateParticipantClicked = (user: User, serviceId: IntegrationServiceId) => {
    setSelectedUser(user)
    setSelectedIntegration(serviceId)
    setShowUpdateParticipantModal(true)
    dispatch(setUpdateStartedFrom('meeting'))
  }

  const handleUpdateParticipantClicked = tracked(
    _handleUpdateParticipantClicked,
    {
      eventName: TrackEvent.CRM_CONTACT_UPDATE_OPENED,
      eventDetails: {
        ...getRoomTrackingDetails(roomSession),
      },
    },
    {
      eventDetailsExtractor: (user: User, serviceId: string) => {
        return {
          userId: user.id,
          serviceId,
        }
      },
    },
  )

  const handleParticipantConsentClicked = (user: User, consent: UserConsent | null) => {
    setSelectedUser(user)
    setCurrentConsent(consent)
    setShowConsentModal(true)
  }

  const handleShowAddMissingParticipantModal = () => {
    setShowAddMissingParticipantModal(true)
  }

  const handleAddMissingParticipantSuccess = async () => {
    setShowAddMissingParticipantModal(false)

    if (!roomId || !sessionId) {
      return
    }

    await refreshRoomSession()
  }

  const handleAddMissingParticipantCancel = () => {
    setShowAddMissingParticipantModal(false)
  }

  const handleDeleteManualParticipantClicked = useCallback(
    (user: User, manualParticipant: ManualParticipant) => {
      if (!canManagePrivateTeammateAccess && manualParticipant.userType === 'managed') {
        return
      }

      setManualParticipantToDelete({ user, manualParticipant })
    },
    [canManagePrivateTeammateAccess],
  )

  const handleEditManualParticipantClicked = useCallback((user: User, manualParticipant: ManualParticipant) => {
    setManualParticipantToEdit({ user, manualParticipant })
  }, [])

  const handleEditManualParticipantSuccess = useCallback(async () => {
    setManualParticipantToEdit(null)
    await refreshRoomSession()
  }, [refreshRoomSession])

  const handleEditManualParticipantCancel = useCallback(() => {
    setManualParticipantToEdit(null)
  }, [])

  const handleDeleteManualParticipantConfirm = useCallback(async () => {
    if (!manualParticipantToDelete || !roomId || !sessionId) {
      setManualParticipantToDelete(null)
      return
    }

    await deleteManualParticipant({
      accountId,
      roomId,
      sessionId,
      userId: manualParticipantToDelete.user.id,
    }).unwrap()

    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: `${getUserDisplayName(manualParticipantToDelete.user)} removed` }))

    setManualParticipantToDelete(null)
    await refreshRoomSession()
  }, [dispatch, accountId, deleteManualParticipant, manualParticipantToDelete, refreshRoomSession, roomId, sessionId])

  const handleDeleteManualParticipantCancel = useCallback(() => {
    setManualParticipantToDelete(null)
  }, [])

  if (!roomSession || isLoading) {
    return (
      <Container className='mx-0 w-100 mw-100 p-0'>
        <Row className='p-0'>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={4} bg={'light'} />
          </Placeholder>
        </Row>
        <Row className='mt-3 d-flex flex-column zocks-gap-1.25'>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={2} bg={'light'} />
          </Placeholder>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={1} bg={'light'} />
          </Placeholder>
          <Placeholder className='p-0 assignment-placeholder' as={Col} animation={'wave'}>
            <Placeholder xs={1} bg={'light'} />
          </Placeholder>
        </Row>
      </Container>
    )
  }

  const EditButton: React.FC<{ title?: React.ReactNode | null }> = ({ title = null }) => (
    <ScopedBlock
      scope={{
        scope: 'allocation:create',
        superScope: ['allocation:admin'],
      }}
      missingScopeAction={MissingScopeAction.HIDE}
    >
      <Tooltip title={!hasTranscription ? 'Meeting has no transcription' : undefined}>
        <Button
          variant='grayDarkGhostButton'
          className={classNames({
            'p-1': !title,
            'p-2': title,
          })}
          onClick={handleShowAssignmentModal}
          disabled={disableAllocation}
        >
          <i className='bi bi-gear' /> {title}
        </Button>
      </Tooltip>
    </ScopedBlock>
  )

  const toggleSentimentAnalysis = () => {
    setShowParticipantSentimentAnalysis((prevState) => !prevState)
  }

  const UpdateParticipantButton: React.FC<{ user: User; serviceId: IntegrationServiceId }> = ({ user, serviceId }) => {
    const { contactUpdateSyncStatus } = useSyncStatuses()
    const userIntegration = useMemo(() => {
      return user.integrations?.find((integration) => integration.serviceId === serviceId)
    }, [user.integrations, serviceId])
    const hasSyncedContact = useMemo(() => {
      return contactUpdateSyncStatus.some((status) => status.serviceId === serviceId && status.externalId === userIntegration?.externalId)
    }, [contactUpdateSyncStatus, serviceId, userIntegration])

    if (user.id.startsWith('speaker')) {
      return (
        <Tooltip
          className='paragraph-small'
          title={'Configure Participant Assignments by clicking the gear icon before creating or updating contact data in connected systems.'}
          placement='top'
          offset={[0, 10]}
        >
          <Button variant='header' className='p-1' disabled={true}>
            <span className='position-relative d-inline-flex'>
              <IntegrationIcon serviceId={serviceId as IntegrationServiceId} width='1rem' height='1rem' />
              {hasSyncedContact ? <i className='export-result-crm-synced-badge bi bi-circle-fill' /> : null}
            </span>
          </Button>
        </Tooltip>
      )
    }

    return (
      <Button variant='header' className='p-1' onClick={() => handleUpdateParticipantClicked(user, serviceId)} disabled={disableAllocation}>
        <span className='position-relative d-inline-flex'>
          <IntegrationIcon serviceId={serviceId as IntegrationServiceId} width='1rem' height='1rem' />
          {hasSyncedContact ? <i className='export-result-crm-synced-badge bi bi-circle-fill' /> : null}
        </span>
      </Button>
    )
  }

  const Participant = ({ user, consent, showSentiment = false }: { user: User; consent: UserConsent | null; showSentiment: boolean }) => {
    const { id: userId, type } = user
    const userType = currentUserId === userId ? 'current' : (type ?? 'anonymous')
    const isAnonymous = userType === 'anonymous'
    const isExternal = userType === 'external'
    const roomSession = useAppSelector(selectRoomSession)
    const [showEditButton, setShowEditButton] = useState<boolean>(false)
    const { hasQuota } = useZocksQuota()
    const hasMultiLanguageSupportQuota = hasQuota('ai.multiLanguageSupport.value')
    const { multiLanguageEnabled } = useMultiLanguageEnabled()
    const manualParticipant = manualParticipantsByUserId[userId] ?? null
    const canRemoveManualParticipant = manualParticipant && (manualParticipant.userType !== 'managed' || canManagePrivateTeammateAccess)
    const showParticipantActions = manualParticipant ? showEditButton : isAnonymous || showEditButton

    const mouseEnterHandler = () => {
      setShowEditButton(true)
    }

    const mouseLeaveHandler = () => {
      setShowEditButton(false)
    }

    const participantSentimentAnalysis = useMemo(() => {
      if (!roomSession) {
        return null
      }

      const metadata = parseMetadata(roomSession.metadata) as SentimentAnalysisMetadata
      const participants = metadata?.sentimentAnalysis?.participants ?? null
      if (!participants) {
        return null
      }
      return participants[userId] ?? null
    }, [roomSession, userId])

    return (
      <div
        className={classNames({
          'assignment-border': !isAnonymous,
          'assignment-anonymous-border': isAnonymous,
          'sentiment-opened': showSentiment,
        })}
      >
        <Container className='mx-0 participant-container'>
          <Row className='p-2 d-flex justify-content-between align-items-center' onMouseEnter={mouseEnterHandler} onMouseLeave={mouseLeaveHandler}>
            <Col className='p-0' xs>
              <Container className='p-0 d-flex flex-column gap-1'>
                <Row className='participant-name-container p-0 d-flex gap-2 mx-0'>
                  <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                    <div className='assignment-person-icon'>
                      <i className='bi bi-person-fill' />
                    </div>
                    {type === 'external' ? (
                      <NavLink
                        className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium`}
                        to={`/library/manage-contacts/contacts/${userId}`}
                      >
                        <div className='paragraph-medium bold'>{getUserDisplayName(user)}</div>
                      </NavLink>
                    ) : (
                      <div className='paragraph-medium bold'>{getUserDisplayName(user)}</div>
                    )}
                  </Col>
                  <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                    <UserTag className='zocks-py-0.125 px-2' userType={userType} />
                    {manualParticipant ? (
                      <ManualParticipantIndicator
                        user={user}
                        manualParticipant={manualParticipant}
                        manualParticipantAddedByNames={manualParticipantAddedByNames}
                        onEditClicked={handleEditManualParticipantClicked}
                      />
                    ) : null}
                    {user.settings?.language && hasMultiLanguageSupportQuota && multiLanguageEnabled ? (
                      <ContactLanguageDisplay language={user.settings.language} shortLabel />
                    ) : null}
                    <SpeakingDistributionIcon userType={userType} percentage={getSpeakingPercentage(user)} />
                    {consentTrackingAvailable &&
                      account?.effectiveConfig.complianceConfig.userConsentTrackingEnabled &&
                      user.roles.every((role) => !CONSENT_EXCLUDED_ROLES.includes(role.id)) &&
                      consent && (
                        <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                          <ConsentIcon
                            contact={user}
                            consent={consent}
                            showText={false}
                            role='button'
                            onClick={() => handleParticipantConsentClicked(user, consent)}
                          />
                        </Col>
                      )}
                    {(isAnonymous || isExternal) && currentUser?.integrations
                      ? currentUser.integrations
                          .filter((e) => CONTACT_UPDATE_SUPPORTED_INTEGRATIONS.includes(e.serviceId as IntegrationServiceId))
                          .map(({ serviceId }) => <UpdateParticipantButton key={serviceId} user={user} serviceId={serviceId} />)
                      : null}
                  </Col>
                </Row>
              </Container>
            </Col>
            <Col className={classNames('p-0', { invisible: !showParticipantActions })} xs='auto'>
              <div className='d-flex align-items-center gap-2'>
                {canRemoveManualParticipant ? (
                  <Button
                    type='button'
                    variant='grayDarkGhostButton'
                    className='p-1'
                    aria-label='Delete manual participant'
                    onClick={() => handleDeleteManualParticipantClicked(user, manualParticipant)}
                  >
                    <i className='bi bi-trash3' />
                  </Button>
                ) : null}
                <EditButton />
              </div>
            </Col>
          </Row>
          {account && account.effectiveConfig.sentimentAnalysis && participantSentimentAnalysis && showSentiment && (
            <Container className='p-0'>
              <Row className='sentiment-analysis-container'>
                <Col className='p-0 mt-4'>
                  <div className='px-3 d-flex flex-row align-items-center gap-4'>
                    <SentimentDistributionInfo distribution={participantSentimentAnalysis.distribution} useEmojis={true} ignoreZero={true} />
                  </div>
                  <div>
                    <SentimentTrendChart timeline={participantSentimentAnalysis.timeline} width='100%' height={'100px'} />
                  </div>
                </Col>
              </Row>
            </Container>
          )}
        </Container>
      </div>
    )
  }

  const getParticipantsBody = (
    usersByRoles: {
      [key: string]: User[]
    },
    usersConsent: UsersConsent | null,
    showSentiment: boolean,
  ) => {
    return (
      <>
        {Object.entries(usersByRoles).map(([role, users]) => {
          return (
            <Col key={role} xs={12} md={12 / ROLES_PER_PAGE} className='d-flex flex-column p-0'>
              <div className='py-2 lead-extra-small'>
                {role} ({users.length})
              </div>
              <div className='d-flex flex-column gap-1 participants-container'>
                {users.map((user) => {
                  return <Participant key={user.id} user={user} consent={usersConsent ? usersConsent[user.id] : null} showSentiment={showSentiment} />
                })}
              </div>
            </Col>
          )
        })}
      </>
    )
  }

  return (
    <>
      {showAssignmentModal && (
        <AllocationModal
          show={showAssignmentModal}
          roomId={roomSession.roomId}
          sessionId={roomSession.id}
          onSaveNewAllocation={handleSaveNewAssignment}
          onCancelNewAllocation={handleCancelNewAssignment}
        />
      )}
      {showUpdateParticipantModal && selectedUser && selectedIntegration && (
        <UpdateExternalMeetingContactModal
          show={showUpdateParticipantModal}
          user={selectedUser}
          serviceId={selectedIntegration}
          onCancel={handleCancelParticipantUpdate}
          onSave={handleSaveParticipantUpdate}
        />
      )}
      {selectedUser && showConsentModal && (
        <ConsentModal
          show={showConsentModal}
          consent={currentConsent}
          onCancel={() => setShowConsentModal(false)}
          contact={selectedUser}
          roomId={roomId}
          sessionId={sessionId}
        />
      )}
      {manualParticipantToEdit ? (
        <EditManualParticipantModal
          show={true}
          accountId={accountId}
          roomId={roomId ?? ''}
          sessionId={sessionId ?? ''}
          user={manualParticipantToEdit.user}
          manualParticipant={manualParticipantToEdit.manualParticipant}
          usersConsent={usersConsent}
          consentTrackingEnabled={consentWarningsEnabled}
          onCancel={handleEditManualParticipantCancel}
          onSuccess={handleEditManualParticipantSuccess}
        />
      ) : null}
      {manualParticipantToDelete ? (
        <DeleteManualParticipantModal
          show={true}
          participantName={getUserDisplayName(manualParticipantToDelete.user)}
          showUnshareWarning={roomSession.isPrivate && manualParticipantToDelete.manualParticipant.userType === 'managed'}
          loading={deletingManualParticipant}
          onCancel={handleDeleteManualParticipantCancel}
          onConfirm={handleDeleteManualParticipantConfirm}
        />
      ) : null}
      {showAddMissingParticipantModal && roomId && sessionId && (
        <AddParticipantModal
          show={showAddMissingParticipantModal}
          roomId={roomId}
          sessionId={sessionId}
          meetingUsers={roomSession.users}
          usersConsent={usersConsent}
          consentTrackingEnabled={consentWarningsEnabled}
          canManagePrivateTeammateAccess={canManagePrivateTeammateAccess}
          isPrivate={roomSession.isPrivate}
          onCancel={handleAddMissingParticipantCancel}
          onSuccess={handleAddMissingParticipantSuccess}
        />
      )}
      <Container className='mx-0 w-100 mw-100 p-0'>
        <Row className='p-0'>
          <Col className='d-flex flex-column gap-2 p-0'>
            <span className='lead-medium'>Participants ({roomSession.users.length})</span>
            <div className='d-flex gap-2'>
              <ScopedBlock
                scope={{
                  scope: 'allocation:create',
                  superScope: ['allocation:admin'],
                }}
                missingScopeAction={MissingScopeAction.HIDE}
              >
                <Tooltip title={!hasTranscription ? 'Meeting has no transcription' : undefined}>
                  <Button
                    variant='grayDarkGhostButton'
                    className='p-2 d-flex align-items-center zocks-gap-0.5'
                    onClick={handleShowAddMissingParticipantModal}
                    disabled={disableAllocation}
                  >
                    <i className='bi bi-person-plus' />
                    <span className='button-medium'>Add participant</span>
                  </Button>
                </Tooltip>
              </ScopedBlock>
              <EditButton title={<span className='button-medium'>Assign participants</span>} />
              <SentimentAnalysisToggleButton onToggleSentimentAnalysis={toggleSentimentAnalysis} showSentimentAnalysis={showParticipantSentimentAnalysis} />
            </div>
          </Col>
        </Row>
        <Row className='mt-3 d-none d-lg-flex zocks-gap-1.25'>
          {getParticipantsBody(usersByRolesOnCurrentPage, usersConsent, showParticipantSentimentAnalysis)}
        </Row>
        <Row className='mt-3 d-flex d-lg-none zocks-gap-1.25'>{getParticipantsBody(usersByRoles, usersConsent, showParticipantSentimentAnalysis)}</Row>
        {pages > 1 ? (
          <Row className='d-none d-lg-flex justify-content-end'>
            <Col className='p-0 d-flex zocks-gap-0.75 align-items-center' xs='auto'>
              <Button variant='link' className='page-button' disabled={currentPage === 1} onClick={previousPage}>
                <i className='bi bi-chevron-left' />
              </Button>
              <div className='lead-extra-small'>
                {currentPage}/{pages}
              </div>
              <Button variant='link' className='page-button' disabled={currentPage === pages} onClick={nextPage}>
                <i className='bi bi-chevron-right' />
              </Button>
            </Col>
          </Row>
        ) : null}
      </Container>
    </>
  )
}
